import React from 'react';
import { Link } from 'react-router-dom';
import { Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { formatPrice } from '../../../shared/utils/format-price.util';
import { formatShortDate } from '../../../shared/utils/format-date.util';
import { ADMIN_USERS_APP_PATHS } from '../../../users/constants/users-paths.constants';
import { InvitationDetails } from '../../types/invitation.type';

type CardLine = { key: string; label: string; value: React.ReactNode };

type Props = {
  invitation: InvitationDetails;
};

const EMPTY_VALUE = '-';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    gap: '2rem',

    '& > section': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },

    '& h4': {
      margin: 0,
      opacity: 0.7,
    },
  },
  paperContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    flex: 1,
    padding: '1rem 0.75rem',

    '& p': {
      margin: 0,
    },
  },
  label: {
    fontWeight: 'bold',
    color: '#424242',
  },
  link: {
    color: theme.palette.admin.primary.main,
    textDecoration: 'none',

    '&:hover': {
      textDecoration: 'underline',
    },
  },
  deletedSender: {
    color: '#757575',
    fontStyle: 'italic',
  },
}));

const formatDelivery = ({ deliveryDate, deliverySchedule }: InvitationDetails) => {
  if (!deliveryDate) return EMPTY_VALUE;

  const formattedDate = formatShortDate(deliveryDate);
  return deliverySchedule ? `${formattedDate}, ${deliverySchedule}` : formattedDate;
};

const trimText = (value?: string) => value?.trim() ?? '';

const formatAddress = ({ deliveryAddress }: InvitationDetails) => {
  if (!deliveryAddress) return EMPTY_VALUE;

  const cityLine = [deliveryAddress.zipCode, deliveryAddress.city].map(trimText).filter(Boolean).join(' ');
  const parts = [trimText(deliveryAddress.address), cityLine].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : EMPTY_VALUE;
};

const formatComplement = ({ deliveryAddress }: InvitationDetails) =>
  trimText(deliveryAddress?.complement) || EMPTY_VALUE;

const formatPickupSchedule = ({ ecoInvitationSettings }: InvitationDetails) => {
  const pickupSchedule = ecoInvitationSettings?.pickupSchedule;
  if (!pickupSchedule?.from || !pickupSchedule?.to) return EMPTY_VALUE;

  return `${pickupSchedule.from} - ${pickupSchedule.to}`;
};

function SummaryCard({ title, lines }: { title: string; lines: CardLine[] }) {
  const classes = useStyles();

  return (
    <section>
      <h4>{title}</h4>
      <Paper className={classes.paperContent}>
        {lines.map((line) => (
          <p key={line.key}>
            <span className={classes.label}>{line.label} :</span> {line.value}
          </p>
        ))}
      </Paper>
    </section>
  );
}

export function InvitationSummaryCards({ invitation }: Props) {
  const classes = useStyles();
  const { sender } = invitation;

  const informationsLines: CardLine[] = [
    {
      key: 'sender',
      label: 'Éco-acteur',
      value: sender ? (
        <Link className={classes.link} to={ADMIN_USERS_APP_PATHS.USER_DETAILS(sender.id)}>
          {sender.firstName} {sender.lastName}
        </Link>
      ) : (
        <span className={classes.deletedSender}>Compte supprimé</span>
      ),
    },
    { key: 'createdAt', label: 'Créée le', value: formatShortDate(invitation.createdAt) },
    { key: 'delivery', label: 'Livraison le', value: formatDelivery(invitation) },
    { key: 'pickupSchedule', label: 'Créneau de retrait', value: formatPickupSchedule(invitation) },
    { key: 'address', label: 'Adresse', value: formatAddress(invitation) },
    { key: 'complement', label: 'Complément', value: formatComplement(invitation) },
    { key: 'message', label: 'Message', value: invitation.ecoInvitationSettings?.customMessage || EMPTY_VALUE },
  ];

  const paymentLines: CardLine[] = [
    { key: 'totalPaid', label: 'Total payé', value: formatPrice(invitation.totalPaid) },
    {
      key: 'paidOrders',
      label: 'Commandes payées',
      value: `${invitation.paidOrdersCount} / ${invitation.ordersCount}`,
    },
    { key: 'contacts', label: 'Contacts', value: invitation.contactsCount },
  ];

  return (
    <div className={classes.root}>
      <SummaryCard title="Informations" lines={informationsLines} />
      <SummaryCard title="Paiement" lines={paymentLines} />
    </div>
  );
}
